import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { I18nService } from '@app/i18n/services';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback';
import { LegalsService } from '@services/legals/legals.service';
import { ProgressIndicatorService } from '@services/progress';

import { IAppKey } from '../../../../../../services/appkey';
import { DialogClass } from '../../../../../../services/dialog';
import { LegalAssociateAppKey } from './legal-associate-appkey.models';

@Component({
	selector: 'app-legal-associate-appkey',
	templateUrl: './legal-associate-appkey.dialog.html',
	styleUrls: ['./legal-associate-appkey.dialog.scss']
})
export class LegalAssociateAppKeyDialogComponent implements OnInit, AfterViewInit {
	@ViewChild('appKeySelect') appKeySelect: MatSelect;
	@ViewChild(MatSort, { static: false }) sort: MatSort;
	@ViewChild(MatTable) table: MatTable<any>;

	public tableColumns: string[] = ['name', 'provider', 'clusterName', 'clientId', 'action'];
	public formInputs = this.formBuilder.group({
		provider: [''],
		cluster: [''],
		appKeys: ['']
	});
	public providers: string[] = [];
	public clusters: { name: string; id: string }[] = [];
	public appKeys: IAppKey[] = [];
	public selectedAppKeys: IAppKey[] = [];

	constructor(
		private legalsService: LegalsService,
		private i18nService: I18nService,
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService,
		@Inject(MAT_DIALOG_DATA) public dialogData: LegalAssociateAppKey,
		private dialogRef: MatDialogRef<LegalAssociateAppKeyDialogComponent>,
		private formBuilder: FormBuilder
	) {}

	public ngOnInit(): void {
		this.appKeys = [...this.dialogData.appKeys].sort((a, b) => {
			return a.name.localeCompare(b.name);
		});

		this.providers = this.appKeys
			.reduce((acc, value) => {
				return !acc.includes(value.provider) ? [...acc, value.provider] : acc;
			}, [])
			.sort((a, b) => {
				return a.localeCompare(b);
			});

		this.clusters = this.appKeys
			.reduce((acc, value) => {
				return acc.findIndex((cluster) => cluster.id === value.clusterId) === -1
					? [
							...acc,
							{
								id: value.clusterId,
								name: value.clusterName ? value.clusterName : value.clusterId
							}
					  ]
					: acc;
			}, [])
			.sort((a, b) => {
				return a.name.localeCompare(b.name);
			});

		this.formInputs.controls.provider.valueChanges.subscribe(() => this.filterAppKeys());
		this.formInputs.controls.cluster.valueChanges.subscribe(() => this.filterAppKeys());
		this.formInputs.controls.appKeys.valueChanges.subscribe((value) => {
			if (value) {
				this.selectedAppKeys = [...this.selectedAppKeys, value].sort((a, b) => {
					return a.name.localeCompare(b.name);
				});

				this.appKeys = this.appKeys.filter((appKey) => appKey.clientId !== value.clientId);
				this.formInputs.controls.appKeys.setValue('', { emitEvent: false });
				this.sortList();
				this.resizeDialog();
			}
		});
	}

	public ngAfterViewInit(): void {
		this.sort.sortChange.subscribe((value: { active: string; direction: string }) => {
			this.sortList(value.active, value.direction);
		});
	}

	public selectAll(): void {
		this.selectedAppKeys = [...this.selectedAppKeys, ...this.appKeys].sort((a, b) => {
			return a.name.localeCompare(b.name);
		});
		this.appKeys = [];
		this.appKeySelect.close();
		this.sortList();
		this.resizeDialog();
	}

	public onFormSubmit(): void {
		this.feedbackService.confirm({
			title: FeedbackTitle.attention,
			message: this.i18nService.get(
				'This will associate the legal document with the selected app keys. Are you sure you want to continue?',
				'app.prompt.legal.associate-terms'
			),
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) {
					this.associateToAppKeys();
				}
			}
		});
	}

	public removeAppKey(appKey: IAppKey): void {
		this.selectedAppKeys = this.selectedAppKeys.filter((selectedAppKey) => selectedAppKey.clientId !== appKey.clientId);
		this.filterAppKeys();
		this.resizeDialog();
	}

	public removeAllAppKeys(): void {
		this.selectedAppKeys = [];
		this.filterAppKeys();
		this.resizeDialog();
	}

	private resizeDialog(): void {
		if (this.selectedAppKeys.length > 0) {
			this.dialogRef.addPanelClass(DialogClass.large);
			this.dialogRef.removePanelClass(DialogClass.medium);
		} else {
			this.dialogRef.removePanelClass(DialogClass.large);
			this.dialogRef.addPanelClass(DialogClass.medium);
		}
	}

	private sortList(active = this.sort.active, direction = this.sort.direction.toString()): void {
		this.selectedAppKeys.sort((a, b) => {
			return direction === 'asc'
				? b[active].localeCompare(a[active])
				: direction === 'desc'
				? a[active].localeCompare(b[active])
				: a.name.localeCompare(b.name);
		});

		this.table.renderRows();
	}

	private filterAppKeys(): void {
		this.appKeys = this.dialogData.appKeys
			.filter((value) => {
				return this.selectedAppKeys.findIndex((appKey) => appKey.clientId === value.clientId) === -1;
			})
			.filter((value) => {
				return this.formInputs.controls.provider.value ? value.provider === this.formInputs.controls.provider.value : true;
			})
			.filter((value) => {
				return this.formInputs.controls.cluster.value ? value.clusterId === this.formInputs.controls.cluster.value : true;
			});
	}

	private associateToAppKeys(): void {
		this.progressIndicatorService.spinner(true);

		const clientIds = this.selectedAppKeys.map((appKey) => appKey.clientId);

		this.legalsService.associateTermToAppKeys(clientIds, this.dialogData.terms.termsId).subscribe(
			() => {
				this.dialogData.callback();
				this.dialogRef.close();
				this.feedbackService.notification({
					msg: this.i18nService.get('App keys associated with legal.', 'app.prompt.legal.app-keys-associated'),
					config: { duration: 4000 }
				});
			},
			(error: any) => {
				this.progressIndicatorService.spinner(false);
				this.feedbackService.apiError(error);
			}
		);
	}
}
